import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useRecaptcha } from "@/hooks/useRecaptcha";

// Import new components
import { RegistrationDateGuard } from "@/components/registration/RegistrationDateGuard";
import { ResumeUploadSection } from "@/components/registration/ResumeUploadSection";
import { CandidateRegistrationForm } from "@/components/registration/CandidateRegistrationForm";
import { InvalidCollegeScreen } from "@/components/registration/InvalidCollegeScreen";
import { SuccessScreen } from "@/components/registration/SuccessScreen";
import { RegistrationHeader } from "@/components/registration/RegistrationHeader";
import { PositionSelectionStep } from "@/components/registration/PositionSelectionStep";
import { ConfirmEmailDialog } from "@/components/registration/ConfirmEmailDialog";
import { ServerTimeProvider } from "@/hooks/useServerTime";
import { CANDIDATE_STATUS, COLLEGE_STATUS, EVALUATION_ROUNDS } from "@/types/enums";
import { codesMatch, normalizeCollegeCode } from "@/utils/collegeCode";
const candidateSchema = z.object({
  full_name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(10, "Phone number must be at least 10 digits"),
  cgpa: z.number().min(0).max(10, "CGPA must be between 0 and 10").optional(),
  branch: z.string().optional(),
  qualification: z.string().optional(),
  latest_semester_score: z.number().min(0).max(100).optional(),
  enrollment_number: z.string().min(1, "Enrollment number is required"),
  github_url: z.string().url().optional().or(z.literal("")),
  linkedin_url: z.string().url().optional().or(z.literal("")),
  permanent_address: z
    .string()
    .min(1, "Permanent address is required")
    .max(200, "Permanent address must be at most 200 characters"),
  permanent_city: z.string().min(2, "City must be at least 2 characters"),
  current_address: z.string().optional(),
  current_city: z.string().optional(),
  position_applied: z.string().optional(),
  key_skills: z.array(z.string()).optional(),
  technologies: z.array(z.string()).optional(),
  hobbies_interests: z.array(z.string()).optional(),
  projects_details: z.array(z.string()).optional(),
  project_links: z.array(z.string()).optional(),
  internships_experience: z.array(z.string()).optional(),
  certifications_awards: z.array(z.string()).optional(),
  event_participations: z.array(z.string()).optional(),
});

export type CandidateFormData = z.infer<typeof candidateSchema>;

declare global {
  interface Window {
    grecaptcha: any;
  }
}

// Data sanitization function to handle Unicode issues
const sanitizeData = (data: any): any => {
  if (typeof data === "string") {
    return data.replace(
        // eslint-disable-next-line no-control-regex
      /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g,
      ""
    );
  }
  if (Array.isArray(data)) {
    return data.map(sanitizeData);
  }
  if (data && typeof data === "object") {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      sanitized[key] = sanitizeData(value);
    }
    return sanitized;
  }
  return data;
};

// Function to get client IP address
const getClientIP = async (): Promise<string> => {
  try {
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error("Failed to get IP address:", error);
    return "";
  }
};

function PublicCandidateRegistrationContent() {
  const { collegeCode } = useParams();
  const normalizedCollegeCode = normalizeCollegeCode(collegeCode);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isRecaptchaLoaded, siteKey } = useRecaptcha();

  // State management
  const [currentStep, setCurrentStep] = useState<"position-selection" | "form">(
    "position-selection"
  );
  const [selectedPosition, setSelectedPosition] = useState<string>("");
  const [isVerifyingCaptcha, setIsVerifyingCaptcha] = useState(false);
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isParsingResume, setIsParsingResume] = useState(false);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [clientIP, setClientIP] = useState<string>("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<CandidateFormData | null>(null);

  const [parsedResumeData, setParsedResumeData] = useState(null);
  const [markDownText, setMarkDownText] = useState(null);
  const [resumeParseStatus, setResumeParseStatus] = useState<
    "idle" | "parsing" | "success" | "error"
  >("idle");

  const form = useForm<CandidateFormData>({
    resolver: zodResolver(candidateSchema),
    defaultValues: {
      enrollment_number: "",
      key_skills: [],
      technologies: [],
      hobbies_interests: [],
      projects_details: [],
      project_links: [],
      internships_experience: [],
      certifications_awards: [],
      event_participations: [],
    },
  });

  // Get client IP on component mount
  useEffect(() => {
    const fetchIP = async () => {
      const ip = await getClientIP();
      setClientIP(ip);
    };
    fetchIP();
  }, []);

  const { data: college, isLoading: collegeLoading } = useQuery({
    queryKey: ["college-by-code", normalizedCollegeCode],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("colleges")
        .select("*")
        .eq("is_deleted", false)
        .eq("is_enabled", true)
        .eq("status", COLLEGE_STATUS.ACTIVE)
        .order("created_at", { ascending: false });

      if (error) throw error;

      return (data || []).find((item) =>
        codesMatch(item.college_code, normalizedCollegeCode)
      ) ?? null;
    },
    enabled: Boolean(normalizedCollegeCode),
  });

  const { data: jobPositions, isLoading: jobPositionsLoading } = useQuery({
    queryKey: ["job-positions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_positions")
        .select("*")
        .eq("is_active", true)
        .order("role_name");

      if (error) throw error;
      return data;
    },
  });

  // Function to upload resume to R2 via edge function
  const uploadResumeToR2 = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("file", file);

    const { data, error } = await supabase.functions.invoke("upload-resume", {
      body: formData,
    });

    if (error) {
      throw new Error(`Failed to upload resume: ${error.message}`);
    }

    if (!data.url) {
      throw new Error("No URL returned from upload service");
    }

    return data.url;
  };

  // Function to verify reCAPTCHA
  const verifyRecaptcha = async (): Promise<boolean> => {
    if (!isRecaptchaLoaded || !window.grecaptcha) {
      toast({
        title: "reCAPTCHA not loaded",
        description: "Please wait for reCAPTCHA to load and try again.",
        variant: "destructive",
      });
      return false;
    }

    try {
      setIsVerifyingCaptcha(true);

      const token = await window.grecaptcha.execute(
        "6LePJWArAAAAAFhS2-d9lHkMzwPrFfzumfdsCswH",
        { action: "resume_upload" }
      );

      const { data: verifyResult, error } = await supabase.functions.invoke(
        "verify-recaptcha",
        { body: JSON.stringify({ token }) }
      );

      if (error) {
        console.error("reCAPTCHA verification error:", error);
        toast({
          title: "Verification failed",
          description: "Failed to verify reCAPTCHA. Please try again.",
          variant: "destructive",
        });
        return false;
      }

      if (verifyResult.success) {
        return true;
      } else {
        toast({
          title: "Security check failed",
          description: "Please try uploading your resume again.",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error("reCAPTCHA error:", error);
      toast({
        title: "Security verification failed",
        description:
          "Please try again or contact support if the problem persists.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsVerifyingCaptcha(false);
    }
  };

  // Function to parse resume and prefill form
  const parseResumeAndPrefill = async (file: File, position: string) => {
    try {
      setIsParsingResume(true);
      setResumeParseStatus("parsing");

      // First verify reCAPTCHA before processing the resume
      // const isRecaptchaValid = await verifyRecaptcha();
      // if (!isRecaptchaValid) {
      //   setResumeParseStatus("error");
      //   setIsParsingResume(false);
      //   return;
      // }

      // Upload resume to R2 storage
      const uploadedUrl = await uploadResumeToR2(file);
      setResumeUrl(uploadedUrl);

      // Parse the uploaded PDF on the server to reduce client-side failures
      const { data: parseResult, error: parseError } =
        await supabase.functions.invoke("parse-resume-gemini", {
          body: {
            fileUrl: uploadedUrl,
            positionApplied: position,
          },
        });

      if (parseError) {
        console.error("Resume parsing error:", parseError);
        setResumeParseStatus("error");
        toast({
          title: "Resume parsing failed",
          description:
            "Retry parsing the resume or continue after reviewing your details.",
          variant: "destructive",
        });
        return;
      }

      if (parseResult?.success && parseResult?.data) {
        const data = parseResult.data;
        setParsedResumeData(data);
        setMarkDownText(parseResult.markdown ?? data.resume_markdown ?? null);
        setResumeParseStatus("success");

        // Auto-fill form with parsed data, ensuring proper number formatting
        if (data.full_name) form.setValue("full_name", data.full_name);
        if (data.email) form.setValue("email", data.email);
        if (data.phone) form.setValue("phone", data.phone);
        if (data.cgpa) {
          const cgpaValue = parseFloat(data.cgpa);
          if (!isNaN(cgpaValue) && cgpaValue >= 0 && cgpaValue <= 10) {
            form.setValue("cgpa", Math.round(cgpaValue * 100) / 100);
          }
        }
        if (data.branch) form.setValue("branch", data.branch);
        if (data.qualification)
          form.setValue("qualification", data.qualification);
        if (data.latest_semester_score) {
          const scoreValue = parseFloat(data.latest_semester_score);
          if (!isNaN(scoreValue) && scoreValue >= 0 && scoreValue <= 100) {
            form.setValue(
              "latest_semester_score",
              Math.round(scoreValue * 100) / 100
            );
          }
        }
        if (data.github_url) form.setValue("github_url", data.github_url);
        if (data.linkedin_url) form.setValue("linkedin_url", data.linkedin_url);
        if (data.permanent_address) form.setValue("permanent_address", data.permanent_address);
        if (data.permanent_city) form.setValue("permanent_city", data.permanent_city);
        if (data.current_address) form.setValue("current_address", data.current_address);
        if (data.current_city) form.setValue("current_city", data.current_city);
        if (data.key_skills?.length)
          form.setValue("key_skills", data.key_skills);
        if (data.technologies?.length)
          form.setValue("technologies", data.technologies);
        if (data.hobbies_interests?.length)
          form.setValue("hobbies_interests", data.hobbies_interests);
        if (data.projects_details?.length)
          form.setValue("projects_details", data.projects_details);
        if (data.project_links?.length)
          form.setValue("project_links", data.project_links);
        if (data.internships_experience?.length)
          form.setValue("internships_experience", data.internships_experience);
        if (data.certifications_awards?.length)
          form.setValue("certifications_awards", data.certifications_awards);

        // Set the selected position
        form.setValue("position_applied", position);

        // Move to form step after successful parsing
        setCurrentStep("form");

        toast({
          title: "Resume parsed successfully!",
          description:
            "Form has been auto-filled. Please review and update the information below.",
        });
      } else {
        setResumeParseStatus("error");
        toast({
          title: "Resume parsing failed",
          description:
            "Retry parsing the resume or continue after reviewing your details.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Resume parsing failed:", error);
      setResumeParseStatus("error");
      toast({
        title: "Resume parsing failed",
        description:
          "Retry parsing the resume or continue after reviewing your details.",
        variant: "destructive",
      });
    } finally {
      setIsParsingResume(false);
    }
  };

  const submitMutation = useMutation({
    mutationFn: async (data: CandidateFormData) => {
      const sanitizedData = sanitizeData(data);

      const selectedJobPosition = jobPositions?.find(
        (pos) => pos.role_name === selectedPosition
      );

      const candidateData = {
        full_name: sanitizedData.full_name,
        email: sanitizedData.email,
        phone: sanitizedData.phone,
        college_id: college?.id,
        college_university: college?.name,
        parsed_resume_data: sanitizeData(parsedResumeData),
        resume_markdown: sanitizeData(markDownText),
        resume_url: resumeUrl,
        status: CANDIDATE_STATUS.CV_SUBMITTED,
        current_round: EVALUATION_ROUNDS.CV_PROFILE,
        ip_address: clientIP, // Capture IP address
        cgpa: sanitizedData.cgpa
          ? Math.round(sanitizedData.cgpa * 100) / 100
          : null,
        latest_semester_score: sanitizedData.latest_semester_score
          ? Math.round(sanitizedData.latest_semester_score * 100) / 100
          : null,
        branch: sanitizedData.branch || "",
        qualification: sanitizedData.qualification || "",
        enrollment_number: sanitizedData.enrollment_number,
        github_url: sanitizedData.github_url || "",
        linkedin_url: sanitizedData.linkedin_url || "",
        permanent_address: sanitizedData.permanent_address || "",
        permanent_city: sanitizedData.permanent_city || "", // Add permanent city
        current_address: sanitizedData.current_address || "",
        current_city: sanitizedData.current_city || "",
        position_applied:
          selectedPosition || sanitizedData.position_applied || "",
        job_position_id: selectedJobPosition?.id || null, // Store the job position ID
        key_skills: sanitizedData.key_skills || [],
        technologies: sanitizedData.technologies || [],
        hobbies_interests: sanitizedData.hobbies_interests || [],
        projects_details: sanitizedData.projects_details || [],
        project_links: sanitizedData.project_links || [],
        internships_experience: sanitizedData.internships_experience || [],
        certifications_awards: sanitizedData.certifications_awards || [],
        event_participations: sanitizedData.event_participations || [],
      };

      const { data: candidate, error } = await supabase
        .from("candidates")
        .insert(candidateData)
        .select()
        .single();

      if (error) throw error;

      // Trigger CV evaluation using Gemini function with position context
      supabase.functions
        .invoke("evaluate-cv-gemini", {
          body: {
            candidateId: candidate.id,
            positionApplied: selectedPosition || sanitizedData.position_applied,
          },
        })
        .catch((evaluationError) => {
          console.error(
            "Failed to trigger background CV evaluation:",
            evaluationError
          );
        });

      return candidate;
    },
    onSuccess: () => {
      setShowSuccess(true);
      toast({
        title: "Registration completed successfully!",
        description: "Your profile is being evaluated.",
      });
    },
    onError: (error: any) => {
      let errorMessage = "Registration failed. Please try again.";

      // Check for duplicate key violation
      if (error.code === "23505") {
        // Check which constraint was violated
        if (error.message.includes("candidates_email_key")) {
          errorMessage =
            "This email is already registered. Please use a different email or contact support if you believe this is an error.";
        } else if (error.message.includes("candidates_enrollment_number_key")) {
          errorMessage =
            "This enrollment number is already registered. Please verify your details or contact support if you believe this is an error.";
        } else if (error.message.includes("candidates_phone_key")) {
          errorMessage =
            "This phone number is already registered. Please use a different number or contact support if you believe this is an error.";
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        title: "Registration failed",
        description: errorMessage,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file && selectedPosition) {
      setResumeFile(file);
      setResumeParseStatus("idle");
      await parseResumeAndPrefill(file, selectedPosition);
    } else if (file && !selectedPosition) {
      toast({
        title: "Position required",
        description: "Please select a position before uploading your resume.",
        variant: "destructive",
      });
    }
  };

  const handlePositionSelect = (position: string) => {
    setSelectedPosition(position);
    form.setValue("position_applied", position);
  };

  const handleContinueAfterUpload = () => {
    if (!selectedPosition) {
      toast({
        title: "Position required",
        description: "Please select a position to continue.",
        variant: "destructive",
      });
      return;
    }

    if (!resumeUrl) {
      toast({
        title: "Resume required",
        description: "Upload your resume before continuing to the form.",
        variant: "destructive",
      });
      return;
    }

    form.setValue("position_applied", selectedPosition);
    setCurrentStep("form");
  };

  const handleRetryParsing = () => {
    if (resumeFile && selectedPosition) {
      parseResumeAndPrefill(resumeFile, selectedPosition);
    }
  };

  const handleFormSubmit = (data: CandidateFormData) => {
    if (!resumeUrl) {
      toast({
        title: "Resume upload required",
        description: "Please upload your resume before submitting the form.",
        variant: "destructive",
      });
      return;
    }

    setPendingFormData(data);
    setShowConfirmDialog(true);
  };

  const handleConfirmSubmit = (email: string) => {
    if (!pendingFormData) return;
    if (email.toLowerCase() !== pendingFormData.email.toLowerCase()) {
      toast({
        title: "Email mismatch",
        description: "The entered email does not match the form email.",
        variant: "destructive",
      });
      return;
    }
    setShowConfirmDialog(false);
    setIsSubmitting(true);
    submitMutation.mutate(pendingFormData);
  };

  const handleCancelConfirm = () => {
    setShowConfirmDialog(false);
    setPendingFormData(null);
  };

  // Add this handler before the return statement
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  // Conditional Rendering
  if (collegeLoading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        Loading...
      </div>
    );
  if (!college) {
    return <InvalidCollegeScreen collegeCode={normalizedCollegeCode} />;
  }
  if (showSuccess) return <SuccessScreen onGoHome={() => navigate("/")} />;

  return (
    <RegistrationDateGuard college={college} isLoading={collegeLoading}>
      <div className="min-h-screen bg-gray-50 py-8" onKeyDown={handleKeyDown}>
        <div className="container mx-auto px-4 max-w-4xl">
          <RegistrationHeader collegeName={college.name} />

          {currentStep === "position-selection" && (
            <PositionSelectionStep
              jobPositions={jobPositions || []}
              isLoadingJobPositions={jobPositionsLoading}
              selectedPosition={selectedPosition}
              onPositionSelect={handlePositionSelect}
              resumeFile={resumeFile}
              parseStatus={resumeParseStatus}
              isVerifyingCaptcha={isVerifyingCaptcha}
              recaptchaLoaded={isRecaptchaLoaded}
              onFileChange={handleFileUpload}
              onRetry={handleRetryParsing}
              onContinue={handleContinueAfterUpload}
              canContinue={!!resumeUrl && !isParsingResume && !isVerifyingCaptcha}
            />
          )}

          {currentStep === "form" && (
            <Form {...form}>
              <CandidateRegistrationForm
                collegeName={college.name}
                jobPositions={jobPositions || []}
                isLoadingJobPositions={jobPositionsLoading}
                onSubmit={handleFormSubmit}
                isSubmitting={isSubmitting || resumeParseStatus === "parsing"}
                selectedPosition={selectedPosition}
              />
            </Form>
          )}

          <ConfirmEmailDialog
            open={showConfirmDialog}
            email={pendingFormData?.email || form.watch("email")}
            loading={isSubmitting}
            onCancel={handleCancelConfirm}
            onConfirm={handleConfirmSubmit}
          />
        </div>
      </div>
    </RegistrationDateGuard>
  );
}

export default function PublicCandidateRegistration() {
  return (
    <ServerTimeProvider>
      <PublicCandidateRegistrationContent />
    </ServerTimeProvider>
  );
}
